import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppShell } from '../app-shell';
import { StashBoardHeader, Scope } from './StashBoardHeader';
import { ScopePicker } from './ScopePicker';
import { SearchBar } from './SearchBar';
import { NearMeControls } from './NearMeControls';
import { CategoryGrid } from './CategoryGrid';
import { StashCardList } from './StashCardList';
import { LoadingOverlay } from './LoadingOverlay';
import { useStashScopedStore } from '../../store/stash-scoped/context';
import { useTopLevelCategoryViewModels, useStashCardPresentations } from '../../store/stash-scoped/hooks';
import type { StashCardPeopleScope, Stasher, StashCategory } from '../../store/stash-scoped/types';

function toUiScope(scope: StashCardPeopleScope): Scope {
  if (scope.kind === 'stasher') return 'me';
  if (scope.kind === 'group') return 'friends';
  return 'all';
}

function toPeopleScope(
  uiScope: Scope,
  currentUserStasherId: string,
  stashers: Stasher[],
): StashCardPeopleScope {
  if (uiScope === 'me') return { kind: 'stasher', stasherId: currentUserStasherId };
  if (uiScope === 'friends') {
    const friendIds = stashers
      .map(s => s.recordID)
      .filter(id => id !== currentUserStasherId);
    return { kind: 'group', stasherIds: friendIds };
  }
  return { kind: 'all' };
}

export function StashBoard() {
  const navigate = useNavigate();
  const [listStyle, setListStyle] = useState<'grid' | 'list'>('grid');
  const [nearMeEnabled, setNearMeEnabled] = useState(false);
  const [nearMeRadius, setNearMeRadius] = useState(5);

  const scope = useStashScopedStore(s => s.scope);
  const loading = useStashScopedStore(s => s.loading);
  const changeScope = useStashScopedStore(s => s.changeScope);
  const currentUserStasherId = useStashScopedStore(s => s.currentUserStasherId);
  const stashers = useStashScopedStore(s => s.stashers);

  const categoryViewModels = useTopLevelCategoryViewModels();
  const stashCardPresentations = useStashCardPresentations();

  // Grid tiles route to the new Category Detail page; no in-board drill-down,
  // so the breadcrumb is always just "STASH Board" and there's no top-bar back
  // button to register here.
  const { setBreadcrumb } = useAppShell();
  useEffect(() => {
    setBreadcrumb(['STASH Board']);
  }, [setBreadcrumb]);

  const uiScope = toUiScope(scope);

  const categoryCounts = Object.fromEntries(
    categoryViewModels.map(vm => [vm.category.recordID, vm.vendorIdsInScope.length]),
  );

  const handleCategoryClick = (category: StashCategory) => {
    navigate(`/app/categories/${category.recordID}`, { state: { category } });
  };

  const currentUser = {
    displayName: 'Russ Z.',
    avatarUrl: null as string | null,
  };

  const homeBase = {
    name: 'Buckhead',
    displayName: 'Buckhead',
  };

  const handleScopeChange = (newScope: string) => {
    // ScopePicker only ever emits 'all' | 'friends' | 'me', which is exactly Scope
    changeScope(toPeopleScope(newScope as Scope, currentUserStasherId, stashers));
  };

  return (
    <div>
      {/* Sticky top section */}
      <div className="sticky top-0 z-20 bg-background/80 backdrop-blur-xl border-b border-border/50 pb-3">
        <StashBoardHeader
          scope={uiScope}
          homeBase={homeBase}
          currentUser={currentUser}
          nearMeEnabled={nearMeEnabled}
          onToggleNearMe={() => setNearMeEnabled(!nearMeEnabled)}
          onAddCard={() => navigate('/app/snap')}
          onShareApp={() => console.log('Share app')}
          onCreateGroup={() => console.log('Create group')}
          onCycleHomeBase={(dir: string) => console.log('Cycle home base', dir)}
          onProfileClick={() => console.log('Profile click')}
        />

        <ScopePicker
          activeScope={uiScope}
          homeBaseName={homeBase.displayName}
          onScopeChange={handleScopeChange}
        />

        <SearchBar
          onSearch={(q: string) => console.log('Search', q)}
          listStyle={listStyle}
          onListStyleChange={(s: string) => setListStyle(s as 'grid' | 'list')}
        />

        <NearMeControls
          enabled={nearMeEnabled && uiScope === 'all'}
          radiusMiles={nearMeRadius}
          location="Buckhead, Atlanta"
          onRadiusChange={(r: number) => setNearMeRadius(r)}
          onDisable={() => setNearMeEnabled(false)}
        />
      </div>

      {/* Content area — grid by default, flat card list when listStyle === 'list'.
          (The previous in-board category drill-down was removed in favor of the
          dedicated `/app/categories/:categoryId` page.) */}
      <div className="relative overflow-hidden">
        <LoadingOverlay visible={loading} />

        {listStyle !== 'grid' ? (
          <StashCardList
            cards={stashCardPresentations}
            onCardClick={(card) => navigate(`/app/stashcards/${card.id}`, { state: { presentation: card } })}
          />
        ) : (
          <CategoryGrid
            categories={categoryViewModels.map(vm => vm.category)}
            counts={categoryCounts}
            onCategoryClick={handleCategoryClick}
          />
        )}
      </div>
    </div>
  );
}
